import exportFromJSON from 'export-from-json';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  VscCloudDownload,
  VscListUnordered,
  VscNewFile,
} from 'react-icons/vsc';
import { useDispatch, useSelector } from 'react-redux';
import DataGrid from '../../UI/DataGrid/DataGrid';
import AccesRestreins from '../../../hoc/AccesRestreins/AccesRestreins';
import { checkMot } from '../../../ressources/functionJS/checkMot';
import * as privilege from '../../../ressources/glossaires/glossaire_privileges';
import * as requestType from '../../../ressources/glossaires/glossaire_requetes';
import * as type_btn from '../../../ressources/glossaires/glossaire_type_de_bouton';
import * as actions from '../../../services/actions';
import Bouton from '../../UI/Bouton/Bouton';
import Popup from '../../UI/Popup/Popup';
import Toolbar from '../../UI/Toolbar/Toolbar';
import SensorType from '../SensorType/SensorType';
import S from '../Capteur.module.scss';
import styles from './TableauCapteur.module.scss';

// Column definitions factory
const createColumn = (field, labelKey, width) => ({
  field,
  headerName: checkMot(labelKey),
  width,
  sortable: true,
  align: 'center',
  headerAlign: 'center',
});

// Column configuration
const COLUMN_CONFIG = [
  ['sensor_id', 'capteur_id', 120],
  ['project_id', 'projet_id', 120],
  ['type_id', 'id_type', 100],
  ['sensor_name', 'nom', 200],
  ['sensors_group', 'groupe_de_capteurs', 180],
  ['remarks', 'remarques', 200],
  ['format_date', 'format_date', 170],
  ['path_ascii_file', 'chemin_du_fichier_ascii', 250],
  ['position', 'position', 150],
  ['import_filter_id', 'filtre_importation_id', 180],
  ['emailgroup_id', 'id_du_groupe_d_email', 180],
  ['active', 'activite_du_capteur', 150],
  ['sensor_projection', 'repere_du_capteur', 180],
  ['coordinate_x', 'coordonnee_x', 130],
  ['coordinate_y', 'coordonnee_y', 130],
  ['coordinate_z', 'coordonnee_z', 130],
];

const TableauCapteur = ({ creer, selectioneLigne }) => {
  const dispatch = useDispatch();

  // Redux state
  const listeCapteurs = useSelector((state) => state.capteur.capteursListe);
  const filtre_projects_list_id = useSelector(
    (state) => state.authentifie.filtre_projects_list_id
  );
  const project_id_url = useSelector(
    (state) => state.interfaceClient.project_id_url
  );
  const interfaceAdmin = useSelector(
    (state) => state.authentifie.interfaceAdmin
  );

  // Local state
  const [listExport, setListExport] = useState([]);
  const [selectionModel, setSelectionModel] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  // Memoized columns
  const columns = useMemo(
    () =>
      COLUMN_CONFIG.map(([field, labelKey, width]) =>
        createColumn(field, labelKey, width)
      ),
    []
  );

  // Effect for initial data fetch
  useEffect(() => {
    const projectIds =
      !isNaN(project_id_url) && project_id_url !== ''
        ? [parseInt(project_id_url)]
        : filtre_projects_list_id;

    if (projectIds.length > 0) {
      dispatch(actions.reqRecupererListeCapteurs(projectIds));
    }
  }, [project_id_url, filtre_projects_list_id, dispatch]);

  // Handle row selection
  const handleSelectionModelChange = useCallback(
    (newSelection) => {
      setSelectionModel(newSelection);
      const selectedRows =
        listeCapteurs?.filter((row) => newSelection.includes(row.sensor_id)) ||
        [];
      setListExport(selectedRows);
    },
    [listeCapteurs, selectioneLigne]
  );

  const handleRowSelect = useCallback(
    (params) => {
      selectioneLigne(params.row);
    },
    [selectioneLigne]
  );

  // Export configuration
  const exporterConfiguration = useCallback(() => {
    exportFromJSON({
      data: { sensors_list: listExport },
      fileName: 'ArgosCapteurListExport',
      exportType: 'json',
    });
  }, [listExport]);

  // Refresh handler
  const handleRefresh = useCallback(() => {
    dispatch(actions.reqRecupererListeCapteurs(filtre_projects_list_id));
  }, [dispatch, filtre_projects_list_id]);

  // Search handlers
  const handleSearchChange = useCallback((e) => {
    setSearchValue(e.target.value);
  }, []);

  const handleSearchClear = useCallback(() => {
    setSearchValue('');
  }, []);

  // Filtered rows with memoization
  const filteredRows = useMemo(() => {
    if (!listeCapteurs) return [];

    return listeCapteurs.filter((capteur) => {
      // Filter by project
      const projectMatch = interfaceAdmin
        ? filtre_projects_list_id.includes(parseInt(capteur.project_id))
        : parseInt(capteur.project_id) === parseInt(project_id_url);

      if (!projectMatch) return false;
      if (!searchValue) return true;

      // Filter by search value
      const searchLower = searchValue.toLowerCase();
      return Object.values(capteur).some((value) =>
        String(value).toLowerCase().includes(searchLower)
      );
    });
  }, [
    listeCapteurs,
    interfaceAdmin,
    filtre_projects_list_id,
    project_id_url,
    searchValue,
  ]);

  return (
    <div className={styles.container}>
      {/* Toolbar */}
      <Toolbar
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        onSearchClear={handleSearchClear}
        searchPlaceholder="recherche"
        onRefresh={handleRefresh}
        refreshRequestType={requestType.recuperation_de_la_liste_des_capteurs}
        refreshMessage={checkMot('requete_en_cours')}
      >
        {/* Create Button with access restrictions */}
        <AccesRestreins
          listNumPrivilege={[
            privilege.creer_des_capteurs,
            privilege.recuperer_list_sensor_type,
          ]}
        >
          <Bouton
            hoverText={checkMot('creer')}
            type={type_btn.outil}
            rond
            cliquer={creer}
          >
            <VscNewFile size={15} />
          </Bouton>
        </AccesRestreins>

        {/* Export & Sensor Type */}
        <AccesRestreins numPrivilege={privilege.modifier_des_capteurs}>
          <Bouton
            type={type_btn.outil}
            rond
            hoverText={checkMot('exporter')}
            cliquer={exporterConfiguration}
          >
            <VscCloudDownload size={15} />
          </Bouton>
          <Popup
            hoverText={checkMot('sensor_type')}
            typeBoutonAffichage={type_btn.outil}
            ajoutOverlay={S.Overlay}
            icone={<VscListUnordered size={15} />}
          >
            <SensorType />
          </Popup>
        </AccesRestreins>
      </Toolbar>

      {/* DataGrid */}
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(row) => row.sensor_id}
        checkboxSelection
        onRowClick={handleRowSelect}
        onRowSelectionModelChange={handleSelectionModelChange}
        rowSelectionModel={selectionModel}
        pageSizeOptions={[25, 50, 100]}
        initialPageSize={25}
        autoRowHeight
      />
    </div>
  );
};

export default TableauCapteur;
